import React, { useState, useEffect } from 'react';
import ProfileTab from '../components/ProfileTab';
import ManageUsersTab from '../components/ManageUsersTab';
import { UserIcon, UsersIcon, ClipboardListIcon, BellIcon } from '@heroicons/react/outline';

function SetupPage() {
  const params = new URLSearchParams(window.location.search);
  const tabParam = params.get('tab');
  let initialTab = 'Profile';
  if (tabParam === 'reports') initialTab = 'Reports';
  else if (tabParam === 'notifications') initialTab = 'Notifications';
  else if (tabParam === 'users') initialTab = 'Manage Users';

  const [activeTab, setActiveTab] = useState(initialTab);
  const [user, setUser] = useState(null);
  const [reportName, setReportName] = useState('');
  const [selectedReport, setSelectedReport] = useState('');
  const [source, setSource] = useState('');
  const [scheduledTime, setScheduledTime] = useState('');
  const [frequency, setFrequency] = useState('Daily');
  const [emails, setEmails] = useState([]);
  const [emailInput, setEmailInput] = useState('');
  const [message, setMessage] = useState('');
  const [reports, setReports] = useState([]);
  const [editId, setEditId] = useState(null);

  const [notifRuleName, setNotifRuleName] = useState('');
  const [selectTag, setSelectTag] = useState('');
  const [ruleOperator, setRuleOperator] = useState('');
  const [notifValue, setNotifValue] = useState('');
  const [cooldown, setCooldown] = useState('');
  const [severity, setSeverity] = useState('');
  const [notifications, setNotifications] = useState([]);
  const [tags, setTags] = useState([]);
  const [notifEditId, setNotifEditId] = useState(null);
  const [notifEmails, setNotifEmails] = useState([]);
  const [notifEmailInput, setNotifEmailInput] = useState('');

  const reportOptions = [
    'Live Dashboard',
    'Metallurgical & KPI',
    'Month To Date',
    'Lab Inspections',
    'Process Equipment Checks',
  ];

  const sourceOptions = [
    '2Seam',
    '4SeamLower',
    '4SeamA',
    '4SeamUpper',
    'MixedOversize',
    'Rewash Coal',
    'All Sources',
  ];

  const frequencyOptions = [
    { value: 'Daily' },
    {
      value: 'Weekly',
      tooltip: 'Report will be sent on Monday of each week for the scheduled time',
    },
    {
      value: 'Monthly',
      tooltip: 'Report will be sent on the last day of the month for the scheduled time',
    },
  ];

  const ruleOptions = [
    'Equal ==',
    'Not Equal !=',
    'Greater >',
    'Greater or Equal >=',
    'Less <',
    'Less or Equal <=',
    'Contains in',
  ];

  const severityOptions = ['Critical', 'Warning'];

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const loadReports = () => {
    fetch('/api/report-setup-list.php')
      .then(res => res.json())
      .then(setReports)
      .catch(() => setReports([]));
  };

  useEffect(loadReports, []);

  useEffect(() => {
    fetch('/api/user.php?action=get_profile')
      .then(res => res.json())
      .then(data => setUser(data.user));
  }, []);

  const loadNotifications = () => {
    fetch('/api/notification-setup-list.php')
      .then(res => res.json())
      .then(setNotifications)
      .catch(() => setNotifications([]));
  };

  const loadTags = () => {
    fetch('/api/notification-tags.php')
      .then(res => res.json())
      .then(setTags)
      .catch(() => setTags([]));
  };

  useEffect(() => {
    if (activeTab === 'Notifications') {
      loadNotifications();
      loadTags();
    }
  }, [activeTab]);

  const addEmail = () => {
    const email = emailInput.trim();
    if (email && emailRegex.test(email) && !emails.includes(email)) {
      setEmails([...emails, email]);
    }
    setEmailInput('');
  };

  const handleEmailKeyDown = e => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      addEmail();
    }
  };

  const removeEmail = email => {
    setEmails(emails.filter(e => e !== email));
  };

  const addNotifEmail = () => {
    const email = notifEmailInput.trim();
    if (email && emailRegex.test(email) && !notifEmails.includes(email)) {
      setNotifEmails([...notifEmails, email]);
    }
    setNotifEmailInput('');
  };

  const handleNotifEmailKeyDown = e => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      addNotifEmail();
    }
  };

  const removeNotifEmail = email => {
    setNotifEmails(notifEmails.filter(e => e !== email));
  };

  const handleEditRow = r => {
    setEditId(r.id || r.schedule_id);
    setReportName(r.report_name || '');
    setSelectedReport(r.report_type || r.selected_report || '');
    setSource(r.source || '');
    setScheduledTime(r.scheduled_time || '');
    setFrequency(r.frequency || 'Daily');
    setEmails(r.email_addresses ? r.email_addresses.split(',') : []);
  };

  const handleDeleteRow = id => {
    if (!window.confirm('Are you sure you want to delete this report?')) return;
    fetch('/api/report-setup-delete.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id }),
    })
      .then(res => res.json())
      .then(loadReports);
  };

  const handleToggleRow = (r) => {
    const newStatus = r.active === 1 ? 0 : 1;
    fetch('/api/report-setup-update.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: r.id || r.schedule_id, active: newStatus }),
    })
      .then(res => res.json())
      .then(() => {
        setMessage(newStatus === 1 ? 'Report activated' : 'Report deactivated');
        setReports(prev =>
          prev.map(item =>
            (item.id || item.schedule_id) === (r.id || r.schedule_id)
              ? { ...item, active: newStatus }
              : item
          )
        );
        loadReports();
      })
      .finally(() => {
        setTimeout(() => setMessage(''), 3000);
      });
  };

  const handleNotificationEditRow = n => {
    setNotifEditId(n.id);
    setNotifRuleName(n.rule_name || '');
    setSelectTag(n.select_tag || '');
    setRuleOperator(n.rule || '');
    setNotifValue(n.value || '');
    setCooldown(n.cooldown || '');
    setSeverity(n.severity || '');
    setNotifEmails(n.email_addresses ? n.email_addresses.split(',') : []);
    setNotifEmailInput('');
  };

  const handleNotificationDeleteRow = id => {
    if (!window.confirm('Are you sure you want to delete this notification?')) return;
    fetch('/api/notification-setup-delete.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id }),
    })
      .then(res => res.json())
      .then(loadNotifications);
  };

  const handleNotificationToggleRow = n => {
    const newStatus = n.active === 1 ? 0 : 1;
    fetch('/api/notification-setup-update.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: n.id, active: newStatus }),
    })
      .then(res => res.json())
      .then(() => {
        setMessage(newStatus === 1 ? 'Notification activated' : 'Notification deactivated');
        setNotifications(prev =>
          prev.map(item => (item.id === n.id ? { ...item, active: newStatus } : item))
        );
        loadNotifications();
      })
      .finally(() => {
        setTimeout(() => setMessage(''), 3000);
      });
  };

  const handleNotificationSave = () => {
    const pendingEmail = notifEmailInput.trim();
    let finalEmails = notifEmails;
    if (pendingEmail && emailRegex.test(pendingEmail) && !notifEmails.includes(pendingEmail)) {
      finalEmails = [...notifEmails, pendingEmail];
      setNotifEmails(finalEmails);
      setNotifEmailInput('');
    }

    if (
      !notifRuleName ||
      !selectTag ||
      !ruleOperator ||
      notifValue === '' ||
      cooldown === '' ||
      !severity ||
      finalEmails.length === 0
    ) {
      setMessage('Please complete all required fields');
      setTimeout(() => setMessage(''), 3000);
      return;
    }
    const payload = {
      rule_name: notifRuleName,
      select_tag: selectTag,
      rule: ruleOperator,
      value: notifValue,
      cooldown,
      severity,
      email_addresses: finalEmails.join(','),
      active: 1,
    };
    const url = notifEditId ? '/api/notification-setup-update.php' : '/api/notification-setup-create.php';
    if (notifEditId) payload.id = notifEditId;

    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
      .then(async res => {
        const data = await res.json().catch(() => null);
        if (!res.ok) throw new Error(data?.error || 'Request failed');
        return data;
      })
      .then(() => {
        setMessage('Saved');
        setNotifRuleName('');
        setSelectTag('');
        setRuleOperator('');
        setNotifValue('');
        setCooldown('');
        setSeverity('');
        setNotifEmails([]);
        setNotifEmailInput('');
        setNotifEditId(null);
        loadNotifications();
      })
      .catch(() => setMessage('Error saving data'))
      .finally(() => {
        setTimeout(() => setMessage(''), 3000);
      });
  };

  const handleSave = () => {
    const pendingEmail = emailInput.trim();
    let finalEmails = emails;
    if (pendingEmail && emailRegex.test(pendingEmail) && !emails.includes(pendingEmail)) {
      finalEmails = [...emails, pendingEmail];
      setEmails(finalEmails);
      setEmailInput('');
    }

    if (
      !reportName ||
      !selectedReport ||
      !scheduledTime ||
      !frequency ||
      finalEmails.length === 0 ||
      ((selectedReport === 'Live Dashboard' || selectedReport === 'Lab Inspections') && !source)
    ) {
      setMessage('Please complete all required fields');
      setTimeout(() => setMessage(''), 3000);
      return;
    }
    const payload = {
      report_name: reportName,
      report_type: selectedReport,
      source,
      scheduled_time: scheduledTime,
      frequency,
      email_addresses: finalEmails.join(','),
      active: 1,
    };
    const url = editId ? '/api/report-setup-update.php' : '/api/report-setup-create.php';
    if (editId) payload.id = editId;

    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
      .then(async res => {
        const data = await res.json().catch(() => null);
        if (!res.ok) throw new Error(data?.error || 'Request failed');
        return data;
      })
      .then(() => {
        setMessage('Saved');
        setReportName('');
        setSelectedReport('');
        setSource('');
        setScheduledTime('');
        setFrequency('Daily');
        setEmails([]);
        setEditId(null);
        loadReports();
      })
      .catch(() => setMessage('Error saving data'))
      .finally(() => {
        setTimeout(() => setMessage(''), 3000);
      });
  };

  const tabs = ['Reports', 'Notifications', 'Profile'];
  if (Number(user?.is_admin) === 1) tabs.push('Manage Users');

  const tabIcons = {
    Reports: ClipboardListIcon,
    Notifications: BellIcon,
    Profile: UserIcon,
    'Manage Users': UsersIcon,
  };

  return (
    <div className="p-4 space-y-4 text-white h-full flex flex-col">
      {message && (
        <div className="fixed top-4 right-4 bg-[#22C55E] text-white px-4 py-2 rounded shadow fade-slide">
          {message}
        </div>
      )}
      <div className="bg-white rounded-lg shadow-lg mt-5 mx-2 mb-2 p-5 text-gray-800 h-[800px] flex flex-col">
        {/* Header */}
        <div className="bg-[#0159B2] text-white text-2xl font-bold px-6 py-3 rounded-t-md text-left -mt-5 -mx-5 mb-4 rounded-b-none">
          Account Setup
        </div>
        {/* Tabs */}
        <div className="border-b flex space-x-2 px-6">
          {tabs.map(tab => {
            const Icon = tabIcons[tab];
            const isActive = activeTab === tab;
            return (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`flex items-center gap-2 px-4 py-2 -mb-px border-b-2 transition-colors duration-200 ${
                  isActive
                    ? 'border-[#22C55E] text-[#0159B2]'
                    : 'border-transparent text-gray-600 hover:text-[#0159B2] hover:border-gray-300'
                }`}
              >
                {Icon && <Icon className="w-5 h-5" />}
                {tab}
              </button>
            );
          })}
        </div>

        <div className="flex-1 overflow-y-auto mt-4 px-6">
          {activeTab === 'Reports' && (
            <form className="space-y-4">
            {/* Report Name */}
            <div>
              <label className="font-semibold text-sm text-gray-700 mb-1 block">
                Report Name
              </label>
              <input
                type="text"
                className="border border-gray-300 rounded px-2 py-2 w-full focus:outline-none focus:border-[#0159B2]"
                value={reportName}
                onChange={e => setReportName(e.target.value)}
              />
            </div>

            {/* Select Report */}
            <div>
              <label className="font-semibold text-sm text-gray-700 mb-1 block">
                Select Report
              </label>
              <select
                className="border border-gray-300 rounded px-2 py-2 w-full focus:outline-none focus:border-[#0159B2]"
                value={selectedReport}
                onChange={e => setSelectedReport(e.target.value)}
              >
                <option value="" disabled hidden>Select...</option>
                {reportOptions.map(option => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>

            {/* Conditional Source */}
            {(selectedReport === 'Live Dashboard' || selectedReport === 'Lab Inspections') && (
              <div>
                <label className="font-semibold text-sm text-gray-700 mb-1 block">
                  Source
                </label>
                <select
                  className="border border-gray-300 rounded px-2 py-2 w-full focus:outline-none focus:border-[#0159B2]"
                  value={source}
                  onChange={e => setSource(e.target.value)}
                >
                  {sourceOptions.map(opt => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Scheduled Time */}
            <div>
              <label className="font-semibold text-sm text-gray-700 mb-1 block">
                Scheduled Time
              </label>
              <input
                type="time"
                className="border border-gray-300 rounded px-2 py-2 w-full focus:outline-none focus:border-[#0159B2]"
                value={scheduledTime}
                onChange={e => setScheduledTime(e.target.value)}
              />
            </div>

            {/* Frequency */}
            <div>
              <label className="font-semibold text-sm text-gray-700 mb-1 block">Frequency</label>
              <div className="flex space-x-4">
                {frequencyOptions.map(opt => (
                  <label
                    key={opt.value}
                    className="inline-flex items-center space-x-1"
                    title={opt.tooltip}
                  >
                    <input
                      type="radio"
                      name="frequency"
                      value={opt.value}
                      checked={frequency === opt.value}
                      onChange={() => setFrequency(opt.value)}
                      className="form-radio text-[#0159B2]"
                    />
                    <span>{opt.value}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Email Addresses */}
            <div>
              <label className="font-semibold text-sm text-gray-700 mb-1 block">
                Email Addresses
              </label>
              <div className="border border-gray-300 rounded p-2 flex flex-wrap">
                {emails.map(email => (
                  <span
                    key={email}
                    className="flex items-center bg-[#0159B2] text-white rounded-full px-2 py-1 text-sm mr-1 mb-1"
                  >
                    {email}
                    <button
                      type="button"
                      onClick={() => removeEmail(email)}
                      className="ml-1 text-white focus:outline-none hover:opacity-75"
                    >
                      ×
                    </button>
                  </span>
                ))}
                <input
                  type="text"
                  className="flex-grow px-2 py-1 focus:outline-none"
                  value={emailInput}
                  onChange={e => setEmailInput(e.target.value)}
                  onKeyDown={handleEmailKeyDown}
                  onBlur={addEmail}
                  placeholder="Add email"
                />
              </div>
            </div>

            <div className="flex justify-center pt-4">
              <button
                type="button"
                onClick={handleSave}
                className="bg-[#22C55E] hover:bg-green-600 text-white px-6 py-3 rounded"
              >
                Save
              </button>
            </div>

            <div className="pt-4">
              <h3 className="text-lg font-semibold mb-2 text-gray-700">Current Reports</h3>
              <div className="overflow-auto max-h-60">
                <table className="min-w-full text-sm text-left">
                  <thead>
                    <tr className="bg-[#0159B2] text-white">
                      <th className="px-2 py-1 first:rounded-l-full">Name</th>
                      <th className="px-2 py-1">Report type</th>
                      <th className="px-2 py-1">Source</th>
                      <th className="px-2 py-1">Scheduled</th>
                      <th className="px-2 py-1">Frequency</th>
                      <th className="px-2 py-1">Email</th>
                      <th className="px-2 py-1">Date</th>
                      <th className="px-2 py-1">Active</th>
                      <th className="px-2 py-1">Edit</th>
                      <th className="px-2 py-1 last:rounded-r-full">Delete</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reports.map(r => (
                      <tr key={r.id || r.schedule_id} className="even:bg-gray-100">
                        <td className="px-2 py-1">{r.report_name}</td>
                        <td className="px-2 py-1">{r.report_type}</td>
                        <td className="px-2 py-1">{r.source}</td>
                        <td className="px-2 py-1">{r.scheduled_time}</td>
                        <td className="px-2 py-1">{r.frequency}</td>
                        <td className="px-2 py-1 break-all">{r.email_addresses}</td>
                        <td className="px-2 py-1">{r.created_at}</td>
                        <td className="px-2 py-1">
                          <div
                            className={`w-10 h-5 rounded-full cursor-pointer transition-colors duration-300 flex items-center ${r.active === 1 ? 'bg-[#22C55E]' : 'bg-gray-400'}`}
                            onClick={() => handleToggleRow(r)}
                          >
                            <div
                              className={`w-4 h-4 bg-white rounded-full shadow transform transition-transform duration-300 ${r.active === 1 ? 'translate-x-5' : 'translate-x-1'}`}
                            />
                          </div>
                        </td>
                        <td className="px-2 py-1">
                          <button
                            type="button"
                            onClick={() => handleEditRow(r)}
                            className="bg-blue-500 text-white font-bold px-3 py-1 rounded-full"
                          >
                            Edit
                          </button>
                        </td>
                        <td className="px-2 py-1">
                          <button
                            type="button"
                            onClick={() => handleDeleteRow(r.id || r.schedule_id)}
                            className="bg-red-500 text-white font-bold px-3 py-1 rounded-full"
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </form>
        )}

          {activeTab === 'Notifications' && (
            <form className="space-y-4">
            <div>
              <label className="font-semibold text-sm text-gray-700 mb-1 block">
                Rule Name
              </label>
              <input
                type="text"
                className="border border-gray-300 rounded px-2 py-2 w-full focus:outline-none focus:border-[#0159B2]"
                value={notifRuleName}
                onChange={e => setNotifRuleName(e.target.value)}
              />
            </div>

            <div>
              <label className="font-semibold text-sm text-gray-700 mb-1 block">
                Select Tag
              </label>
              <input
                list="tagOptions"
                className="border border-gray-300 rounded px-2 py-2 w-full focus:outline-none focus:border-[#0159B2]"
                value={selectTag}
                onChange={e => setSelectTag(e.target.value)}
              />
              <datalist id="tagOptions">
                {tags.map(tag => (
                  <option key={tag} value={tag} />
                ))}
              </datalist>
            </div>

            <div>
              <label className="font-semibold text-sm text-gray-700 mb-1 block">
                Rule
              </label>
              <select
                className="border border-gray-300 rounded px-2 py-2 w-full focus:outline-none focus:border-[#0159B2]"
                value={ruleOperator}
                onChange={e => setRuleOperator(e.target.value)}
              >
                <option value="" disabled hidden>Select...</option>
                {ruleOptions.map(opt => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="font-semibold text-sm text-gray-700 mb-1 block">
                Value
              </label>
              <input
                type="number"
                className="border border-gray-300 rounded px-2 py-2 w-full focus:outline-none focus:border-[#0159B2]"
                value={notifValue}
                onChange={e => setNotifValue(e.target.value)}
              />
            </div>

            <div>
              <label className="font-semibold text-sm text-gray-700 mb-1 block">
                Cooldown
              </label>
              <div className="flex items-center space-x-2">
                <input
                  type="number"
                  className="border border-gray-300 rounded px-2 py-2 w-full focus:outline-none focus:border-[#0159B2]"
                  value={cooldown}
                  onChange={e => setCooldown(e.target.value)}
                />
                <span className="text-gray-700 text-sm">Minutes</span>
              </div>
            </div>

            <div>
              <label className="font-semibold text-sm text-gray-700 mb-1 block">
                Severity
              </label>
              <select
                className="border border-gray-300 rounded px-2 py-2 w-full focus:outline-none focus:border-[#0159B2]"
                value={severity}
                onChange={e => setSeverity(e.target.value)}
              >
                <option value="" disabled hidden>Select...</option>
                {severityOptions.map(opt => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="font-semibold text-sm text-gray-700 mb-1 block">
                Email Addresses
              </label>
              <div className="border border-gray-300 rounded p-2 flex flex-wrap">
                {notifEmails.map(email => (
                  <span
                    key={email}
                    className="flex items-center bg-[#0159B2] text-white rounded-full px-2 py-1 text-sm mr-1 mb-1"
                  >
                    {email}
                    <button
                      type="button"
                      onClick={() => removeNotifEmail(email)}
                      className="ml-1 text-white focus:outline-none hover:opacity-75"
                    >
                      ×
                    </button>
                  </span>
                ))}
                <input
                  type="text"
                  className="flex-grow px-2 py-1 focus:outline-none"
                  value={notifEmailInput}
                  onChange={e => setNotifEmailInput(e.target.value)}
                  onKeyDown={handleNotifEmailKeyDown}
                  onBlur={addNotifEmail}
                  placeholder="Add email"
                />
              </div>
            </div>

            <div className="flex justify-center pt-4">
              <button
                type="button"
                onClick={handleNotificationSave}
                className="bg-[#22C55E] hover:bg-green-600 text-white px-6 py-3 rounded"
              >
                Submit
              </button>
            </div>

            <div className="pt-4">
              <h3 className="text-lg font-semibold mb-2 text-gray-700">Current Notifications</h3>
              <div className="overflow-auto max-h-60">
                <table className="min-w-full text-sm text-left">
                  <thead>
                    <tr className="bg-[#0159B2] text-white">
                      <th className="px-2 py-1 first:rounded-l-full">Rule Name</th>
                      <th className="px-2 py-1">Tag</th>
                      <th className="px-2 py-1">Rule</th>
                      <th className="px-2 py-1">Value</th>
                      <th className="px-2 py-1">Cooldown</th>
                      <th className="px-2 py-1">Severity</th>
                      <th className="px-2 py-1">Email</th>
                      <th className="px-2 py-1">Date</th>
                      <th className="px-2 py-1">Active</th>
                      <th className="px-2 py-1">Edit</th>
                      <th className="px-2 py-1 last:rounded-r-full">Delete</th>
                    </tr>
                  </thead>
                  <tbody>
                    {notifications.map(n => (
                      <tr key={n.id} className="even:bg-gray-100">
                        <td className="px-2 py-1">{n.rule_name}</td>
                        <td className="px-2 py-1">{n.select_tag}</td>
                        <td className="px-2 py-1">{n.rule}</td>
                        <td className="px-2 py-1">{n.value}</td>
                        <td className="px-2 py-1">{n.cooldown}</td>
                        <td className="px-2 py-1">{n.severity}</td>
                        <td className="px-2 py-1 break-all">{n.email_addresses}</td>
                        <td className="px-2 py-1">{n.timestamp}</td>
                        <td className="px-2 py-1">
                          <div
                            className={`w-10 h-5 rounded-full cursor-pointer transition-colors duration-300 flex items-center ${
                              n.active === 1 ? 'bg-[#22C55E]' : 'bg-gray-400'
                            }`}
                            onClick={() => handleNotificationToggleRow(n)}
                          >
                            <div
                              className={`w-4 h-4 bg-white rounded-full shadow transform transition-transform duration-300 ${
                                n.active === 1 ? 'translate-x-5' : 'translate-x-1'
                              }`}
                            />
                          </div>
                        </td>
                        <td className="px-2 py-1">
                          <button
                            type="button"
                            onClick={() => handleNotificationEditRow(n)}
                            className="bg-blue-500 text-white font-bold px-3 py-1 rounded-full"
                          >
                            Edit
                          </button>
                        </td>
                        <td className="px-2 py-1">
                          <button
                            type="button"
                            onClick={() => handleNotificationDeleteRow(n.id)}
                            className="bg-red-500 text-white font-bold px-3 py-1 rounded-full"
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </form>
          )}
          {activeTab === 'Profile' && (
            <ProfileTab user={user} setUser={setUser} setMessage={setMessage} />
          )}
          {activeTab === 'Manage Users' && Number(user?.is_admin) === 1 && (
            <ManageUsersTab user={user} setMessage={setMessage} />
          )}
        </div>
    </div>
  </div>
  );
}

export default SetupPage;

